<script>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useIdleTimeout } from '../composables/useIdleTimeout'

export default {
  setup() {
    const router = useRouter()
    const route = useRoute()
    const sidebarCollapsed = ref(false)

    // 空闲超时：30 分钟无操作自动退出，提前 5 分钟警告
    const { showWarning, remainingSeconds, continueSession, logoutNow } = useIdleTimeout(30, 5)

    const adminUser = computed(() => {
      const user = localStorage.getItem('adminUser')
      return user ? JSON.parse(user) : null
    })

    const menuItems = [
      { path: '/admin/dashboard', label: '仪表盘', icon: '📊' },
      { path: '/admin/products', label: '产品管理', icon: '📦' },
      { path: '/admin/news', label: '新闻管理', icon: '📰' },
      { path: '/admin/cases', label: '案例管理', icon: '📋' },
      { path: '/admin/contacts', label: '联系表单', icon: '📧' },
      { path: '/admin/settings', label: '站点设置', icon: '⚙️' },
    ]

    const isActive = (path) => route.path === path

    const handleLogout = () => {
      localStorage.removeItem('adminUser')
      router.push('/admin/login')
    }

    const toggleSidebar = () => {
      sidebarCollapsed.value = !sidebarCollapsed.value
    }

    const formatTime = (seconds) => {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m}:${s.toString().padStart(2, '0')}`
    }

    return {
      adminUser, menuItems, isActive, handleLogout, toggleSidebar, sidebarCollapsed,
      showWarning, remainingSeconds, continueSession, logoutNow, formatTime,
    }
  }
}
</script>

<template>
  <div class="admin-layout" :class="{ collapsed: sidebarCollapsed }">
    <!-- Sidebar -->
    <aside class="sidebar">
      <div class="sidebar-header">
        <h2 v-if="!sidebarCollapsed">Coronet</h2>
        <h2 v-else>C</h2>
        <button class="toggle-btn" @click="toggleSidebar">
          {{ sidebarCollapsed ? '→' : '←' }}
        </button>
      </div>

      <nav class="sidebar-nav">
        <router-link
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          class="nav-item"
          :class="{ active: isActive(item.path) }"
        >
          <span class="nav-icon">{{ item.icon }}</span>
          <span v-if="!sidebarCollapsed" class="nav-label">{{ item.label }}</span>
        </router-link>
      </nav>

      <div class="sidebar-footer">
        <div v-if="!sidebarCollapsed" class="user-info">
          <div class="user-avatar">{{ adminUser?.displayName?.charAt(0) || 'A' }}</div>
          <div class="user-details">
            <div class="user-name">{{ adminUser?.displayName || 'Admin' }}</div>
            <div class="user-role">管理员</div>
          </div>
        </div>
        <button class="logout-btn" @click="handleLogout" :title="sidebarCollapsed ? '退出' : ''">
          {{ sidebarCollapsed ? '🚪' : '退出登录' }}
        </button>
      </div>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <header class="top-bar">
        <h1 class="page-title">
          {{ menuItems.find(item => isActive(item.path))?.label || '后台管理' }}
        </h1>
        <div class="top-actions">
          <a href="/" target="_blank" class="view-site-btn">查看前台</a>
        </div>
      </header>

      <div class="content-area">
        <router-view />
      </div>
    </main>

    <!-- Idle Timeout Warning Modal -->
    <div v-if="showWarning" class="idle-warning-overlay">
      <div class="idle-warning-modal">
        <div class="idle-warning-icon">⏰</div>
        <h3>会话即将过期</h3>
        <p class="idle-warning-text">
          由于您长时间未操作，系统将在以下时间后自动退出登录：
        </p>
        <div class="idle-warning-countdown">{{ formatTime(remainingSeconds) }}</div>
        <p class="idle-warning-hint">点击下方按钮或进行任意操作以继续会话</p>
        <div class="idle-warning-actions">
          <button class="idle-btn-continue" @click="continueSession">继续操作</button>
          <button class="idle-btn-logout" @click="logoutNow">立即退出</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  background: #f5f6fa;
}

.admin-layout.collapsed .sidebar {
  width: 70px;
}

/* Sidebar */
.sidebar {
  width: 240px;
  background: #1a2d6e;
  color: white;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  z-index: 100;
}

.sidebar-header {
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sidebar-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
}

.toggle-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.sidebar-nav {
  flex: 1;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s;
  font-size: 14px;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-weight: 600;
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.nav-label {
  white-space: nowrap;
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
}

.user-details {
  flex: 1;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
}

.user-role {
  font-size: 12px;
  opacity: 0.7;
}

.logout-btn {
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Main Content */
.main-content {
  flex: 1;
  margin-left: 240px;
  transition: margin-left 0.3s ease;
  display: flex;
  flex-direction: column;
}

.admin-layout.collapsed .main-content {
  margin-left: 70px;
}

.top-bar {
  background: white;
  padding: 14px 32px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1a2d6e;
}

.view-site-btn {
  padding: 8px 16px;
  background: #f0f4ff;
  color: #1a2d6e;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.3s;
}

.view-site-btn:hover {
  background: #e0e7ff;
}

.content-area {
  flex: 1;
  padding: 0 32px 32px;
}

.content-area > div {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.content-area .page-header {
  padding: 20px 0 20px !important;
  background: var(--bg-light) !important;
  text-align: center !important;
  margin-bottom: 20px;
}

/* Idle Timeout Warning */
.idle-warning-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: idle-fade-in 0.3s ease;
}

@keyframes idle-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.idle-warning-modal {
  background: white;
  border-radius: 16px;
  padding: 40px 48px;
  width: 90%;
  max-width: 440px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: idle-slide-up 0.3s ease;
}

@keyframes idle-slide-up {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.idle-warning-icon {
  font-size: 56px;
  margin-bottom: 16px;
  animation: idle-shake 1s ease-in-out infinite;
}

@keyframes idle-shake {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-8deg); }
  75% { transform: rotate(8deg); }
}

.idle-warning-modal h3 {
  margin: 0 0 12px 0;
  font-size: 22px;
  font-weight: 700;
  color: #1a2d6e;
}

.idle-warning-text {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.idle-warning-countdown {
  font-size: 40px;
  font-weight: 800;
  color: #e74c3c;
  font-variant-numeric: tabular-nums;
  margin-bottom: 12px;
  letter-spacing: 2px;
}

.idle-warning-hint {
  margin: 0 0 24px 0;
  font-size: 13px;
  color: #999;
}

.idle-warning-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.idle-btn-continue,
.idle-btn-logout {
  padding: 12px 28px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.idle-btn-continue {
  background: #1a2d6e;
  color: white;
}

.idle-btn-continue:hover {
  background: #2d4499;
}

.idle-btn-logout {
  background: #f5f5f5;
  color: #666;
}

.idle-btn-logout:hover {
  background: #e74c3c;
  color: white;
}
</style>
